(() => {
  const playVideo = (video, targetHeight) => {
    const sources = Array.from(video.querySelectorAll('source'))
      .sort((a, b) => Number(a.dataset.height) - Number(b.dataset.height));
    const source = sources.find((candidate) => Number(candidate.dataset.height) >= targetHeight) || sources.at(-1);
    if (!source) throw new Error('[Product media] Video has no MP4 source.');
    if (video.getAttribute('src') !== source.dataset.src) video.src = source.dataset.src;
    video.play().catch(() => {});
  };

  class BoxedCarousel {
    createCarouselVideo(video) {
      const element = document.createElement('video');
      element.muted = true;
      element.loop = true;
      element.playsInline = true;
      element.preload = 'none';
      element.setAttribute('aria-label', video.alt || '');
      if (video.poster) element.poster = video.poster;
      video.file.sources.filter((source) => source.mime_type === 'video/mp4').forEach((source) => {
        const sourceElement = document.createElement('source');
        sourceElement.dataset.src = source.url;
        sourceElement.dataset.height = source.height;
        sourceElement.type = source.mime_type;
        element.append(sourceElement);
      });
      return element;
    }

    updateCarouselVideoPlayback() {
      this._carouselTrack.querySelectorAll('video').forEach((video) => {
        if (video.hidden || (this._isEmbed && !this._deviceSelected) || (this._desktopMediaQuery.matches && !this._isEmbed)) video.pause();
        else playVideo(video, 720);
      });
    }

    updateDesktopVideoPlayback() {
      this._desktopMediaGrid?.querySelectorAll('video').forEach((video) => {
        if (this._desktopMediaQuery.matches && !this._isEmbed) playVideo(video, 1080);
        else video.pause();
      });
    }

    renderCarousel(images) {
      const carouselImages = Array.isArray(images) ? images : [];
      this._carouselTransitionToken = (this._carouselTransitionToken || 0) + 1;
      this._carouselAnimating = false;
      this._cancelCarouselSwipe?.();
      this._carouselTrack.querySelectorAll('video').forEach((video) => video.pause());
      this._desktopMediaGrid?.querySelectorAll('video').forEach((video) => video.pause());
      this._carouselTrack.replaceChildren();
      this._carouselDots.replaceChildren();
      this._desktopMediaGrid?.replaceChildren();
      this._carouselIndex = 0;

      if (!carouselImages.length) {
        this._carouselElement.hidden = true;
        if (this._desktopMediaGrid) {
          this._desktopMediaGrid.hidden = true;
        }
        return;
      }

      this._carouselElement.hidden = this._isEmbed && !this._deviceSelected;
      if (this._desktopMediaGrid) {
        this._desktopMediaGrid.hidden = this._isEmbed;
      }
      this._carouselPrevious.hidden = carouselImages.length < 2;
      this._carouselNext.hidden = carouselImages.length < 2;

      carouselImages.forEach((image, index) => {
        const responsiveSources = [
          [image.url480, '480w'],
          [image.url720, '720w'],
          [image.url960, '960w'],
          [image.url, '1200w'],
        ].filter(([url]) => url);
        const responsiveSrcset = responsiveSources
          .map(([url, descriptor]) => `${url} ${descriptor}`)
          .join(', ');

        const element = image.type === 'video'
          ? this.createCarouselVideo(image)
          : document.createElement('img');
        if (image.type !== 'video') {
          element.loading = this._isEmbed && !this._deviceSelected ? 'lazy' : image.loading || 'lazy';
          element.src = image.url;
          if (responsiveSrcset) {
            element.srcset = responsiveSrcset;
            element.sizes = this.dataset.carouselSizes || '(min-width: 1024px) 1px, (min-width: 431px) min(calc(100vw - 48px), 760px), calc(100vw - 32px)';
          }
          element.alt = image.alt || '';
          element.width = Number(image.width) || 1200;
          element.height = Number(image.height) || 1200;
          if (index === 0 && !(this._isEmbed && !this._deviceSelected)) element.fetchPriority = 'high';
        }
        element.dataset.carouselImage = '';
        element.hidden = index !== 0;
        this._carouselTrack.append(element);

        const dot = document.createElement('button');
        dot.type = 'button';
        dot.dataset.carouselDot = '';
        dot.setAttribute('aria-label', `${image.type === 'video' ? this.dataset.showVideoLabel : this.dataset.showImageLabel} ${index + 1}`);
        dot.addEventListener('click', () => {
          this.showCarouselImage(index, Math.sign(index - this._carouselIndex));
        });
        this._carouselDots.append(dot);

        if (index < 4 && this._desktopMediaGrid) {
          const desktopMedia = document.createElement('div');
          desktopMedia.className = 'product-boxed__desktop-media';
          const desktopImage = image.type === 'video'
            ? this.createCarouselVideo(image)
            : document.createElement('img');
          if (image.type !== 'video') {
            desktopImage.loading = index === 0 && !this._isEmbed ? 'eager' : 'lazy';
            desktopImage.src = image.url;
            if (responsiveSrcset) {
              desktopImage.srcset = responsiveSrcset;
              desktopImage.sizes = '(min-width: 1024px) min(calc((100vw - 552px) * 0.5), 514px), 1px';
            }
            desktopImage.alt = image.alt || '';
            desktopImage.width = Number(image.width) || 1200;
            desktopImage.height = Number(image.height) || 1200;
            if (index === 0 && !this._isEmbed) desktopImage.fetchPriority = 'high';
          }
          desktopMedia.append(desktopImage);
          this._desktopMediaGrid.append(desktopMedia);
        }
      });

      this.updateDesktopVideoPlayback();
      this.showCarouselImage(0, 0);
    }

    bindCarouselSwipe() {
      let pointerId = null;
      let startX = 0;
      let startY = 0;
      let width = 0;
      let direction = 0;
      let dragging = false;
      let neighbor = null;

      const clearSlide = (slide) => {
        if (!slide) return;
        slide.style.transition = '';
        slide.style.transform = '';
      };

      const clearPointer = () => {
        pointerId = null;
        dragging = false;
        this._carouselDragging = false;
        this._carouselTrack.classList.remove('is-swiping');
      };

      this._cancelCarouselSwipe = () => {
        const slides = Array.from(this._carouselTrack.querySelectorAll('[data-carousel-image]'));
        clearSlide(slides[this._carouselIndex]);
        clearSlide(neighbor);
        if (neighbor && neighbor !== slides[this._carouselIndex]) neighbor.hidden = true;
        if (pointerId !== null) {
          try {
            this._carouselTrack.releasePointerCapture(pointerId);
          } catch {

          }
        }
        neighbor = null;
        direction = 0;
        clearPointer();
      };

      const drawSwipe = (deltaX) => {
        const slides = Array.from(this._carouselTrack.querySelectorAll('[data-carousel-image]'));
        const current = slides[this._carouselIndex];
        if (!current || !deltaX) return;

        const nextDirection = deltaX < 0 ? 1 : -1;
        if (nextDirection !== direction) {
          clearSlide(neighbor);
          if (neighbor) neighbor.hidden = true;
          direction = nextDirection;
          neighbor = slides[(this._carouselIndex + direction + slides.length) % slides.length];
          if (neighbor instanceof HTMLImageElement) neighbor.loading = 'eager';
          neighbor.hidden = false;
        }

        const offset = Math.max(-width, Math.min(width, deltaX));
        current.style.transition = 'none';
        neighbor.style.transition = 'none';
        current.style.transform = `translate3d(${offset}px, 0, 0)`;
        neighbor.style.transform = `translate3d(${offset + direction * width}px, 0, 0)`;
      };

      const finishSwipe = (event, cancelled = false) => {
        if (event.pointerId !== pointerId) return;
        if (!dragging || !neighbor) {
          this._cancelCarouselSwipe();
          return;
        }

        const deltaX = Math.max(-width, Math.min(width, event.clientX - startX));
        if (deltaX) drawSwipe(deltaX);
        const slides = Array.from(this._carouselTrack.querySelectorAll('[data-carousel-image]'));
        const current = slides[this._carouselIndex];
        const incoming = neighbor;
        const swipeDirection = direction;
        const advance = !cancelled
          && Math.sign(deltaX) === -swipeDirection
          && Math.abs(deltaX) >= width * 0.1;
        const nextIndex = (this._carouselIndex + swipeDirection + slides.length) % slides.length;
        clearPointer();
        this._carouselAnimating = true;
        const transitionToken = (this._carouselTransitionToken || 0) + 1;
        this._carouselTransitionToken = transitionToken;

        current.getBoundingClientRect();
        const transition = 'transform 280ms cubic-bezier(0.22, 1, 0.36, 1)';
        current.style.transition = transition;
        incoming.style.transition = transition;
        current.style.transform = `translate3d(${advance ? -swipeDirection * width : 0}px, 0, 0)`;
        incoming.style.transform = `translate3d(${advance ? 0 : swipeDirection * width}px, 0, 0)`;

        let finished = false;
        const complete = () => {
          if (finished) return;
          finished = true;
          window.clearTimeout(timer);
          if (transitionToken !== this._carouselTransitionToken) return;

          if (advance) {
            this._carouselIndex = nextIndex;
            current.hidden = true;
            this._carouselDots.querySelectorAll('[data-carousel-dot]').forEach((dot, index) => {
              const selected = index === nextIndex;
              dot.classList.toggle('is-selected', selected);
              dot.setAttribute('aria-current', selected ? 'true' : 'false');
            });
          } else {
            incoming.hidden = true;
          }
          clearSlide(current);
          clearSlide(incoming);
          neighbor = null;
          direction = 0;
          this._carouselAnimating = false;
          this.updateCarouselVideoPlayback();
        };
        incoming.addEventListener('transitionend', (transitionEvent) => {
          if (transitionEvent.propertyName === 'transform') complete();
        }, { once: true });
        const timer = window.setTimeout(complete, 350);
      };

      this._carouselSwipeEvents?.abort();
      this._carouselSwipeEvents = new AbortController();
      const options = { signal: this._carouselSwipeEvents.signal };
      this._carouselTrack.addEventListener('pointerdown', (event) => {
        if (
          !event.isPrimary
          || (event.pointerType === 'mouse' && event.button !== 0)
          || this._carouselAnimating
          || pointerId !== null
          || this._carouselTrack.querySelectorAll('[data-carousel-image]').length < 2
        ) {
          return;
        }

        pointerId = event.pointerId;
        this._carouselSuppressClick = false;
        startX = event.clientX;
        startY = event.clientY;
        width = this._carouselTrack.getBoundingClientRect().width;
      }, options);

      window.addEventListener('pointermove', (event) => {
        if (event.pointerId !== pointerId) return;
        const deltaX = event.clientX - startX;
        const deltaY = event.clientY - startY;
        if (!dragging) {
          if (Math.abs(deltaY) > 8 && Math.abs(deltaY) > Math.abs(deltaX)) {
            this._cancelCarouselSwipe();
            return;
          }
          if (Math.abs(deltaX) < 8 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
          dragging = true;
          this._carouselDragging = true;
          this._carouselSuppressClick = true;
          try {
            this._carouselTrack.setPointerCapture(pointerId);
          } catch {

          }
          this._carouselTrack.classList.add('is-swiping');
        }
        drawSwipe(deltaX);
      }, options);
      window.addEventListener('pointerup', (event) => finishSwipe(event), options);
      window.addEventListener('pointercancel', (event) => finishSwipe(event, true), options);
    }

    async showCarouselImage(index, direction = Math.sign(index - this._carouselIndex)) {
      const images = Array.from(this._carouselTrack.querySelectorAll('[data-carousel-image]'));
      const dots = Array.from(this._carouselDots.querySelectorAll('[data-carousel-dot]'));

      if (!images.length || this._carouselAnimating || this._carouselDragging) {
        return;
      }

      const nextIndex = (index + images.length) % images.length;
      const previousIndex = this._carouselIndex;
      const updateDots = () => {
        dots.forEach((dot, dotIndex) => {
          const isSelected = dotIndex === this._carouselIndex;
          dot.classList.toggle('is-selected', isSelected);
          dot.setAttribute('aria-current', isSelected ? 'true' : 'false');
        });
      };

      if (direction === 0 || nextIndex === previousIndex) {
        this._carouselIndex = nextIndex;
        images.forEach((image, imageIndex) => {
          image.getAnimations().forEach((animation) => animation.cancel());
          image.classList.remove(
            'is-pushing-out-next',
            'is-pushing-in-next',
            'is-pushing-out-previous',
            'is-pushing-in-previous'
          );
          image.hidden = imageIndex !== nextIndex;
        });
        this.updateCarouselVideoPlayback();
        updateDots();
        return;
      }

      const outgoingImage = images[previousIndex];
      const incomingImage = images[nextIndex];

      if (!outgoingImage || !incomingImage) {
        return;
      }

      const transitionToken = (this._carouselTransitionToken || 0) + 1;
      this._carouselTransitionToken = transitionToken;
      if (incomingImage instanceof HTMLImageElement) incomingImage.loading = 'eager';

      try {
        if (incomingImage instanceof HTMLImageElement && (!incomingImage.complete || incomingImage.naturalWidth === 0)) {
          await incomingImage.decode();
        }
      } catch {

      }

      if (transitionToken !== this._carouselTransitionToken || !incomingImage.isConnected) {
        return;
      }

      this._carouselIndex = nextIndex;
      updateDots();

      this._carouselAnimating = true;
      incomingImage.hidden = false;
      outgoingImage.hidden = false;
      if (incomingImage instanceof HTMLVideoElement && !this._desktopMediaQuery.matches) {
        playVideo(incomingImage, 720);
      }

      images.forEach((image, imageIndex) => {
        if (imageIndex !== previousIndex && imageIndex !== nextIndex) {
          image.hidden = true;
        }
      });

      const outgoingClass = direction > 0
        ? 'is-pushing-out-next'
        : 'is-pushing-out-previous';
      const incomingClass = direction > 0
        ? 'is-pushing-in-next'
        : 'is-pushing-in-previous';

      outgoingImage.classList.add(outgoingClass);
      incomingImage.classList.add(incomingClass);

      await new Promise((resolve) => {
        let finished = false;
        const finish = () => {
          if (finished) {
            return;
          }

          finished = true;
          window.clearTimeout(this._carouselAnimationTimer);
          resolve();
        };

        incomingImage.addEventListener('animationend', finish, { once: true });
        this._carouselAnimationTimer = window.setTimeout(finish, 380);
      });

      if (transitionToken !== this._carouselTransitionToken) {
        return;
      }

      outgoingImage.hidden = true;
      if (outgoingImage instanceof HTMLVideoElement) outgoingImage.pause();
      outgoingImage.classList.remove(outgoingClass);
      incomingImage.classList.remove(incomingClass);
      this._carouselAnimating = false;
    }
  }
  if (typeof window !== 'undefined') window.MarmeladeCarousel = BoxedCarousel.prototype;
  if (typeof module !== 'undefined') module.exports = BoxedCarousel.prototype;
})();
